import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import {
  ContextKind,
  ContextSummary,
  ContextText
} from '@core/models/assistant/assistant.model';
import { ICONS } from '@core/services/icons/icons.service';

export const CONTEXT_ICONS: Record<ContextKind, string> = {
  dataspace: ICONS.database,
  data: ICONS.table,
  planning_space: ICONS.folder,
  plan: ICONS.path
};

export function contextLabel(summary: ContextSummary, translate: TranslateService): string {
  const say = (piece: ContextText) => renderPiece(piece, translate);
  return [summary.name.map(say).join(' '), ...summary.detail.map(say)].join(' · ');
}

function renderPiece(piece: ContextText, translate: TranslateService): string {
  if (!piece.key) return piece.text ?? '';
  const lang = translate.getCurrentLang() || 'en';
  const params = Object.fromEntries(
    Object.entries(piece.params ?? {}).map(([k, v]) => [
      k,
      typeof v === 'number' ? v.toLocaleString(lang) : v
    ])
  );
  return translate.instant(piece.key, params);
}

@Component({
  selector: 'app-assistant-context-text',
  host: { class: 'flex min-w-0 items-baseline gap-1' },
  templateUrl: './assistant-context-text.component.html',
  styles: ``
})
export class AssistantContextTextComponent {
  private readonly translate = inject(TranslateService);
  private readonly lang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.getCurrentLang()
  });
  readonly summary = input.required<ContextSummary>();
  protected readonly parts = computed(() => {
    this.lang();
    const render = (p: ContextText) => ({
      text: renderPiece(p, this.translate),
      mono: !!p.mono
    });
    return {
      name: this.summary().name.map(render),
      detail: this.summary().detail.map(render)
    };
  });
}
